import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { Navigation } from "swiper/modules";
import { ChevronLeft } from "lucide-react";
import { cn, dateFormater, groupDatesByMonthBook } from "@/lib/utils";

type AvailableDate = {
    id: string;
    start_date: Date;
    end_date: Date;
    price: number;
    available_seats?: number;
    expiration_date_time: string | null;
    batchId: string;
}

// Define the types for the props that the component will accept
interface DateGroupSliderProps {
    availableDates: {
        id: string;
        start_date: Date;
        end_date: Date;
        price: number;
        available_seats?: number;
        expiration_date_time: string | null;
        batchId: string;
    }[];  // Array of available dates
    selectedDate: { id: string, start_date: Date; end_date: Date } | null;  // The currently selected date range
    setSelectedDate: React.Dispatch<React.SetStateAction<{ id: string, start_date: Date; end_date: Date } | null>>;  // State setter function to update the selected date
    handleDateChange: (
        e: React.ChangeEvent<HTMLInputElement>,
        date: { id: string; start_date: Date; end_date: Date; price: number }
    ) => void;  // Function to handle the change when a new date is selected
}

// Define the component with correct props typing
const DateGroupSliderBooking: React.FC<DateGroupSliderProps> = ({
    availableDates,
    selectedDate,
    handleDateChange,
}) => {
    // Use the helper function to group dates by month and year
    const groupedDates = groupDatesByMonthBook(availableDates);

    // Convert groupedDates into an array of pairs of month groups
    const groupedKeys = Object.keys(groupedDates);
    const slideGroups = [];
    for (let i = 0; i < groupedKeys.length; i += 2) {
        slideGroups.push([groupedKeys[i], groupedKeys[i + 1]]);
    }

    const monthGroups = Object.entries(groupedDates).reduce((acc, curr, idx) => {
        if (idx % 2 === 0) {
            acc.push([curr]);
        } else {
            // @ts-expect-error because we know the last element is an array
            acc[acc.length - 1].push(curr);
        }
        return acc;
    }, [] as [string, AvailableDate[]][][]);

    // Helper function to check if the date is expired
    const isDateExpired = (date: AvailableDate): boolean => {
        // If no expiration date is set, the date is not expired
        if (!date.expiration_date_time) return false;

        const expirationDateTime = new Date(date.expiration_date_time);
        const currentDateTime = new Date();

        // Compare the full datetime to determine if it's expired
        return currentDateTime > expirationDateTime;
    };

    return (
        <div>
            {slideGroups.length > 0 ? (
                <div className="relative border rounded-lg border-zinc-400 p-4 xl:pt-0 select-none">
                    <Swiper
                        breakpoints={{
                            200: {
                                slidesPerView: 1
                            },
                            // 650: {
                            //     slidesPerView: 2
                            // }
                        }}
                        spaceBetween={12}
                        navigation={{
                            prevEl: `.trip-booking-date-prev`,
                            nextEl: `.trip-booking-date-next`,
                        }}
                        modules={[Navigation]}
                    >
                        {monthGroups.map((group, index) => (
                            <div key={index}>
                                {group.map(([monthYear, dates]) => (
                                    <SwiperSlide key={monthYear}>
                                        <div className="p-0 xl:p-4">
                                            <h2 className="text-center font-medium text-base md:text-lg text-primary-text mb-8 sm:mb-11">{monthYear}</h2>
                                            <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 gap-y-5 xl:gap-5'>
                                                {dates.map((date, i) => {
                                                    const expired = isDateExpired(date);
                                                    return (
                                                        <div key={i} className="flex">
                                                            <input
                                                                type="radio"
                                                                id={date.start_date?.toString()}
                                                                value={`${date.start_date.toISOString()} - ${date.end_date.toISOString()}`}
                                                                checked={
                                                                    selectedDate?.start_date === date.start_date &&
                                                                    selectedDate?.end_date === date.end_date
                                                                }
                                                                onChange={(e) =>
                                                                    handleDateChange(e, {
                                                                        id: date.id,
                                                                        start_date: date.start_date,
                                                                        end_date: date.end_date,
                                                                        price: date.price,
                                                                    })
                                                                }
                                                                disabled={expired || !date.available_seats}
                                                                className="sr-only"
                                                            />
                                                            <label
                                                                htmlFor={date.start_date?.toString()}
                                                                className={cn(
                                                                    "cursor-pointer w-full border rounded-md relative",
                                                                    selectedDate?.start_date === date.start_date && selectedDate?.end_date === date.end_date ? "bg-custom-primary-shade-dark" : "bg-white",
                                                                    (expired || !date.available_seats) ? "cursor-not-allowed border-primary-text/45" : "cursor-pointer border-primary-text/75",
                                                                )}
                                                            >
                                                                <div className='absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center leading-none w-full max-w-[80%]'>
                                                                    {
                                                                        (date.available_seats && !expired) ? (
                                                                            <div
                                                                                className={`font-medium tracking-wider text-xs xl:text-sm leading-none xl:leading-none w-full text-center py-1.5 sm:py-2 rounded-full border border-primary-text/25 
                                                                                    ${date?.available_seats <= 10 ? 'bg-[#EFCA08] text-black' :
                                                                                        'bg-green-500 text-white'
                                                                                    }`}
                                                                            >
                                                                                {date?.available_seats <= 10 ? "Few Lefts" : "Available"}
                                                                            </div>
                                                                        ) : (
                                                                            <p className="text-center font-medium tracking-wider text-xs xl:text-sm bg-red-500 leading-tight text-white w-full py-1.5 sm:py-2 rounded-full border border-primary-text/25">
                                                                                Sold Out
                                                                            </p>
                                                                        )
                                                                    }
                                                                </div>
                                                                <div className={`text-center text-base md:text-lg md: mt-[1.125rem] mb-2 sm:mt-6 sm:mb-2.5 ${expired || !date.available_seats ? 'text-primary-text/55' : 'text-gray-800'}`}>
                                                                    <span>{dateFormater({ date: date.start_date, formatString: "dd MMM" })}</span>{" - "}<span>{dateFormater({ date: date.end_date, formatString: "dd MMM" })}</span>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </div>
                        ))}
                    </Swiper>
                    <button className='trip-booking-date-prev absolute top-4 xl:top-5 left-4 h-6 w-6 p-0.5 z-20 disabled:opacity-50 bg-neutral-200 rounded-full hover:bg-custom-primary hover:text-white disabled:hover:bg-neutral-200 disabled:hover:text-primary-text text-primary-text disabled:cursor-not-allowed'>
                        <ChevronLeft className='h-full w-full' />
                    </button>
                    <button className='trip-booking-date-next absolute top-4 xl:top-5 right-4 h-6 w-6 p-0.5 z-20 disabled:opacity-50 bg-neutral-200 rounded-full hover:bg-custom-primary hover:text-white disabled:hover:bg-neutral-200 disabled:hover:text-primary-text text-primary-text disabled:cursor-not-allowed'>
                        <ChevronLeft className='h-full w-full rotate-180' />
                    </button>
                </div>
            ) : (
                <p>No available dates.</p>
            )}
        </div>
    );
};

export default DateGroupSliderBooking;